import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, X, Globe } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Slider } from "@/components/ui/slider";
import { card, inputCls, btnPrimary, errMsg } from "./shared";
import { PromotionStatsChart } from "./PromotionStatsChart";
import { LocationInsight } from "./LocationInsight";

const EVERYWHERE = "Everywhere";
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

function LocationPicker({ value, onChange, suggestions }) {
  const [draft, setDraft] = useState("");
  const add = (loc) => {
    const v = loc.trim();
    if (v.length >= 2 && !value.some((x) => x.toLowerCase() === v.toLowerCase()) && value.length < 10) onChange([...value, v]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" data-testid="promote-locations">
        {value.map((l) => (
          <span key={l} className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full bg-sky-100 text-sky-900 text-xs font-bold">
            {l}
            <button type="button" aria-label={`Remove ${l}`} data-testid={`promote-loc-remove-${l.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              onClick={() => onChange(value.filter((x) => x !== l))}><X size={12} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="promote-location-input" list="promote-location-options" className={inputCls} placeholder="Type a location, e.g. Accra - Madina"
          value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }} />
        <datalist id="promote-location-options">{suggestions.map((s) => <option key={s.key} value={s.name} />)}</datalist>
        <button type="button" data-testid="promote-location-add" className={`${btnPrimary} h-11 px-4`} onClick={() => add(draft)}>Add</button>
      </div>
      <button type="button" data-testid="promote-everywhere" onClick={() => add(EVERYWHERE)}
        className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 hover:text-sky-950"><Globe size={14} weight="fill" /> Add "Everywhere"</button>
    </div>
  );
}

export function PromotePanel({ promo }) {
  const { refresh } = useAuth();
  const [days, setDays] = useState(promo.min_days);
  const [locs, setLocs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const cost = promo.price_per_day * days * locs.length;

  const loadHistory = () => api.get("/market/business/promotions").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    loadHistory();
    api.get("/market/locations").then(({ data }) => setSuggestions(data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!window.confirm(`Promote in ${locs.join(", ")} for ${days} day(s)?\n${cost} Credits will be deducted.`)) return;
    setBusy(true);
    try {
      await api.post("/market/business/promote", { locations: locs, days });
      toast.success("Your business is now promoted!");
      setLocs([]);
      loadHistory();
      refresh?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const extend = async (p) => {
    const c = promo.price_per_day * days * p.locations.length;
    if (!window.confirm(`Extend this promotion by ${days} day(s)?\n${c} Credits will be deducted.`)) return;
    setBusy(true);
    try {
      await api.post(`/market/business/promotions/${p.promo_id}/extend`, { days });
      toast.success(`Extended by ${days} day(s)`);
      loadHistory();
      refresh?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const now = new Date().toISOString();
  return (
    <div className={`${card} p-5 space-y-4`} data-testid="promote-panel">
      <p className="font-heading font-black text-sky-950 text-lg flex items-center gap-2"><Megaphone size={22} weight="fill" className="text-sky-500" /> Promote your business</p>
      <p className="text-sm text-sky-700">Promoted businesses appear in the Tainapp feed for viewers in the locations you choose.</p>
      <LocationPicker value={locs} onChange={setLocs} suggestions={suggestions} />
      <div className="space-y-2">
        <div className="flex justify-between text-sm font-bold text-sky-900">
          <span>Duration</span><span data-testid="promote-days-value">{days} day{days === 1 ? "" : "s"}</span>
        </div>
        <Slider data-testid="promote-days-slider" min={promo.min_days} max={promo.max_days} step={1} value={[days]} onValueChange={([v]) => setDays(v)} />
      </div>
      <div className="rounded-2xl bg-sky-950 text-white p-4 flex items-center justify-between">
        <div className="text-xs text-white/70">{promo.price_per_day} Credits × {days} day{days === 1 ? "" : "s"} × {locs.length} location{locs.length === 1 ? "" : "s"}</div>
        <div className="font-heading text-3xl font-black text-sky-300" data-testid="promote-cost">{cost.toLocaleString()} <span className="text-sm">Credits</span></div>
      </div>
      <button data-testid="promote-submit" className={`${btnPrimary} w-full`} disabled={busy || locs.length === 0} onClick={submit}>
        {busy ? "Promoting…" : "PROMOTE"}
      </button>
      {history.length > 0 && <PromotionStatsChart refreshKey={history.length} />}
      {history.length > 0 && <LocationInsight refreshKey={history.length} />}
      {history.length > 0 && (
        <div className="space-y-1.5" data-testid="promote-history">
          <p className="text-xs font-bold text-sky-800 uppercase tracking-wider">Your promotions</p>
          {history.map((p) => (
            <div key={p.promo_id} className="flex justify-between gap-3 text-sm border-b border-sky-50 py-1.5 last:border-0">
              <span className="text-sky-900">
                {p.locations.join(", ")}
                <span className="block text-xs text-sky-600" data-testid={`promote-stats-${p.promo_id}`}>{p.views || 0} saw it · {p.taps || 0} tapped</span>
              </span>
              <span className={`shrink-0 font-bold ${p.ends_at > now ? "text-emerald-600" : "text-sky-400"}`}>
                {p.ends_at > now ? `Live until ${fmtDate(p.ends_at)}` : `Ended ${fmtDate(p.ends_at)}`}
                {p.ends_at > now && (
                  <button data-testid={`promote-extend-${p.promo_id}`} disabled={busy} onClick={() => extend(p)}
                    className="block mt-1 ml-auto h-8 px-3 rounded-full bg-sky-500 text-white text-xs font-black hover:bg-sky-600 transition-colors disabled:opacity-50">
                    Extend +{days}d · {promo.price_per_day * days * p.locations.length} Credits
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
